import axios from 'axios';
import MD5 from 'crypto-js/md5';

// Helper to calculate MD5 for Gravatar
const getGravatarUrl = (email) => {
  if (!email) return null;
  const hash = MD5(email.trim().toLowerCase()).toString();
  return `https://www.gravatar.com/avatar/${hash}?d=404`;
};

// Check if Gravatar exists (by trying to load the image)
const checkGravatar = async (email) => {
  if (!email) return { exists: false };
  const url = getGravatarUrl(email);
  try {
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    return { exists: true, url, source: 'Gravatar' };
  } catch (e) {
    return { exists: false };
  }
};

// Extract subreddit name from Reddit URL
const extractSubreddit = (url) => {
  try {
    // Use new RegExp to avoid ESLint no-useless-escape errors with forward slashes in regex literals
    const match = url.match(new RegExp('reddit\\.com/r/([^/]+)'));
    return match ? match[1] : 'unknown';
  } catch (e) {
    return 'unknown';
  }
};

// Reddit Search using Google Custom Search
const searchReddit = async (name, apiKey, cx) => {
  if (!apiKey || !cx) return { results: [], error: 'Missing API Key or CX' };
  
  const redditQueries = [
    `site:reddit.com "${name}"`,
    `site:reddit.com/user/ "${name}"`,
    `site:reddit.com "${name}" profile`
  ];
  
  let redditResults = [];
  
  for (const query of redditQueries) {
    try {
      const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
        params: {
          key: apiKey,
          cx: cx,
          q: query,
          num: 5
        }
      });
      
      const results = (response.data.items || []).map(item => ({
        title: item.title,
        link: item.link,
        summary: item.snippet || '',
        date: new Date().toISOString().split('T')[0],
        source: 'Reddit',
        subreddit: extractSubreddit(item.link)
      }));
      
      redditResults = [...redditResults, ...results];
      if (results.length > 0) break; // Use first successful query
    } catch (error) {
      console.log('Reddit search query failed:', query, error.message);
      continue;
    }
  }
  
  // Remove duplicates and limit to top 5
  const uniqueResults = redditResults.filter((item, index, self) => 
    index === self.findIndex((t) => t.link === item.link)
  ).slice(0, 5);
  
  return {
    results: uniqueResults,
    found: uniqueResults.length > 0
  };
};

// Google Custom Search (Requires API Key)
const searchGoogle = async (query, apiKey, cx) => {
  if (!apiKey || !cx) return { results: [], error: 'Missing API Key or CX' };
  
  try {
    const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
      params: {
        key: apiKey,
        cx: cx,
        q: query,
        num: 10
      }
    });
    
    return {
      results: response.data.items || [],
      found: (response.data.items || []).length > 0
    };
  } catch (error) {
    console.error('Google Search Error:', error);
    return { results: [], error: error.message };
  }
};

// Enhanced Google Image Search with multiple search strategies
const searchGoogleImages = async (name, apiKey, cx) => {
  if (!apiKey || !cx) return { images: [], error: 'Missing API Key or CX' };
  
  const searchStrategies = [
    // Strategy 1: Direct name search
    `"${name}" profile photo`,
    // Strategy 2: Name with portrait
    `"${name}" portrait`,
    // Strategy 3: Name with person keyword
    `"${name}" person photo`,
    // Strategy 4: Simple name search
    `"${name}"`
  ];
  
  let allImages = [];
  
  for (const strategy of searchStrategies) {
    try {
      console.log('Trying image search strategy:', strategy);
      
      const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
        params: {
          key: apiKey,
          cx: cx,
          q: strategy,
          searchType: 'image',
          num: 6, // Fewer images per strategy to avoid duplicates
          safe: 'off',
          imgType: 'photo',
          fileSize: 'medium' // Changed from large to medium for better availability
        },
        timeout: 10000 // 10 second timeout
      });
      
      const strategyImages = (response.data.items || []).map(item => ({
        title: item.title,
        link: item.link,
        thumbnail: item.image?.thumbnailLink || item.link,
        contextLink: item.image?.contextLink || '',
        width: item.image?.width || 0,
        height: item.image?.height || 0,
        size: item.image?.byteSize || 0,
        source: 'Google Images',
        strategy: strategy
      }));
      
      allImages = [...allImages, ...strategyImages];
      
      // If we found images with this strategy, don't try the next ones
      if (strategyImages.length > 0) {
        console.log(`Found ${strategyImages.length} images with strategy: ${strategy}`);
        break;
      }
    } catch (error) {
      console.log(`Strategy failed: ${strategy}`, error.message);
      continue; // Try next strategy
    }
  }
  
  // Remove duplicates based on link
  const uniqueImages = allImages.filter((image, index, self) => 
    index === self.findIndex((img) => img.link === image.link)
  );
  
  // Limit to top 8 unique images
  const limitedImages = uniqueImages.slice(0, 8);
  
  console.log('Final image results:', limitedImages);
  
  return {
    images: limitedImages,
    found: limitedImages.length > 0,
    strategiesTried: searchStrategies.length
  };
};

// Social Media Profile Search
const searchSocialMedia = async (name, apiKey, cx) => {
  if (!apiKey || !cx) return { profiles: {}, error: 'Missing API Key or CX' };
  
  const socialMediaQueries = {
    linkedin: [
      `site:linkedin.com/in/ "${name}"`,
      `site:linkedin.com "${name}" profile`
    ],
    twitter: [
      `site:twitter.com "${name}"`,
      `site:x.com "${name}"`
    ],
    facebook: [
      `site:facebook.com "${name}"`,
      `site:facebook.com/people "${name}"`
    ],
    instagram: [
      `site:instagram.com "${name}"`,
      `site:instagram.com/ "${name}"`
    ],
    tiktok: [
      `site:tiktok.com/@"${name}"`,
      `site:tiktok.com "${name}" profile`
    ]
  };
  
  const socialProfiles = {};
  
  for (const [platform, queries] of Object.entries(socialMediaQueries)) {
    for (const query of queries) {
      try {
        const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
          params: {
            key: apiKey,
            cx: cx,
            q: query,
            num: 3
          }
        });
        
        if (response.data.items && response.data.items.length > 0) {
          const item = response.data.items[0];
          socialProfiles[platform] = {
            link: item.link,
            indicators: ['Found in Search'],
            title: item.title,
            snippet: item.snippet || ''
          };
          break; // Use first successful query for each platform
        }
      } catch (error) {
        console.log(`${platform} search failed:`, error.message);
        continue;
      }
    }
  }
  
  return {
    profiles: socialProfiles,
    found: Object.keys(socialProfiles).length > 0
  };
};

// Fallback image search using Unsplash API (for demo purposes)
const searchUnsplashImages = async (name) => {
  try {
    // This is a demo fallback - in production you'd need an Unsplash API key
    // For now, we'll create placeholder images
    const placeholderImages = [
      {
        title: `Profile photo of ${name}`,
        link: `https://picsum.photos/seed/${name.replace(/\s+/g, '')}1/400/400.jpg`,
        thumbnail: `https://picsum.photos/seed/${name.replace(/\s+/g, '')}1/200/200.jpg`,
        contextLink: '',
        width: 400,
        height: 400,
        size: 0,
        source: 'Demo (Placeholder)',
        strategy: 'Fallback'
      },
      {
        title: `${name} portrait`,
        link: `https://picsum.photos/seed/${name.replace(/\s+/g, '')}2/400/400.jpg`,
        thumbnail: `https://picsum.photos/seed/${name.replace(/\s+/g, '')}2/200/200.jpg`,
        contextLink: '',
        width: 400,
        height: 400,
        size: 0,
        source: 'Demo (Placeholder)',
        strategy: 'Fallback'
      }
    ];
    
    return {
      images: placeholderImages,
      found: true,
      isDemo: true
    };
  } catch (error) {
    console.error('Unsplash fallback error:', error);
    return { images: [], found: false };
  }
};

export const performOsintResearch = async (data, apiConfig) => {
  const { name, email, company } = data;
  const report = {
    stories: [],
    redditPosts: [],
    images: [],
    socialMedia: {},
    contactInfo: { emails: [], phones: [], other: [] },
    redFlags: [],
    score: 0,
    assessment: 'Uncertain'
  };

  console.log('Starting OSINT research for:', name);

  // 1. Check Gravatar (Primary reliable source)
  if (email) {
    const gravatar = await checkGravatar(email);
    if (gravatar.exists) {
      report.contactInfo.emails.push(`${email} (Gravatar Verified)`);
      report.images.push({
        title: 'Gravatar Profile Picture',
        link: gravatar.url,
        thumbnail: gravatar.url,
        contextLink: `https://www.gravatar.com/${MD5(email.trim().toLowerCase()).toString()}`,
        source: 'Gravatar'
      });
      report.score += 25; // Increased points for verified Gravatar
      console.log('Gravatar found');
    } else {
      report.contactInfo.emails.push(`${email} (No Gravatar)`);
      report.redFlags.push('Email not associated with Gravatar account');
    }
  }

  // 2. Reddit Search (if Configured)
  if (apiConfig.googleApiKey && apiConfig.googleCx) {
    console.log('Searching Reddit for:', name);
    const redditData = await searchReddit(name, apiConfig.googleApiKey, apiConfig.googleCx);
    if (redditData.found) {
      report.redditPosts = redditData.results;
      report.score += Math.min(redditData.results.length * 6, 30); // Up to 30 points for Reddit posts
      console.log(`Reddit posts found: ${redditData.results.length}`);
    } else {
      report.redFlags.push('No Reddit activity found');
    }

    // 3. Social Media Search (if Configured)
    console.log('Searching social media for:', name);
    const socialData = await searchSocialMedia(name, apiConfig.googleApiKey, apiConfig.googleCx);
    if (socialData.found) {
      report.socialMedia = socialData.profiles;
      report.score += Math.min(Object.keys(socialData.profiles).length * 8, 40); // Up to 40 points for social media
      console.log(`Social media profiles found: ${Object.keys(socialData.profiles).length}`);
    } else {
      report.redFlags.push('No social media profiles found');
    }

    // 4. Google Search (if Configured) - More reliable than Wikipedia
    console.log('Attempting Google search with provided credentials');
    
    // Primary search with person's name and company
    const searchQueries = [
      `"${name}" ${company || ''}`,
      `"${name}" professional`,
      `"${name}" profile`
    ];
    
    let googleResults = [];
    
    for (const query of searchQueries) {
      const googleData = await searchGoogle(query, apiConfig.googleApiKey, apiConfig.googleCx);
      if (googleData.results.length > 0) {
        googleResults = [...googleResults, ...googleData.results];
        break; // Use first successful query
      }
    }
    
    if (googleResults.length > 0) {
      report.score += Math.min(googleResults.length * 4, 20); // Up to 20 points for general search
      
      googleResults.slice(0, 5).forEach(item => {
        report.stories.push({
          title: item.title,
          url: item.link,
          summary: item.snippet,
          date: new Date().toISOString().split('T')[0]
        });
      });
      console.log('Google search found results');
    }

    // 5. Google Image Search (if Configured) - ALWAYS attempt
    console.log('Attempting image search for:', name);
    const imageData = await searchGoogleImages(name, apiConfig.googleApiKey, apiConfig.googleCx);
    
    if (imageData.found) {
      report.images.push(...imageData.images);
      report.score += Math.min(imageData.images.length * 3, 15); // Up to 15 points for images
      console.log(`Images found: ${imageData.images.length}`);
    } else {
      console.log('No images from Google, trying fallback...');
      // Try fallback demo images
      const fallbackData = await searchUnsplashImages(name);
      if (fallbackData.found) {
        report.images.push(...fallbackData.images);
        report.score += 5; // Small bonus for demo images
        if (fallbackData.isDemo) {
          report.redFlags.push('Showing demo placeholder images (API limits reached)');
        }
      } else {
        report.redFlags.push('No profile images found in search results');
      }
    }
  } else {
    // No API keys - show demo images
    console.log('No API keys, using demo images');
    const demoData = await searchUnsplashImages(name);
    if (demoData.found) {
      report.images.push(...demoData.images);
      report.score += 5;
      report.redFlags.push('Showing demo placeholder images (API keys not provided)');
    }
    
    if (!apiConfig.googleApiKey) {
      report.redFlags.push('Google Search Skipped (No API Key provided)');
    } else if (!apiConfig.googleCx) {
      report.redFlags.push('Google Search Skipped (Missing Search Engine ID - CX)');
    }
    if (!apiConfig.googleApiKey || !apiConfig.googleCx) {
      report.redFlags.push('Comprehensive search disabled (Requires Google API Key and CX)');
    }
  }

  // 6. Enhanced Scoring Logic - More realistic thresholds
  if (report.score === 0) {
    report.assessment = 'Insufficient Data';
    report.redFlags.push('No public digital footprint found in accessible sources');
  } else if (report.score < 40) {
    report.assessment = 'Likely Fake / Private';
  } else if (report.score < 70) {
    report.assessment = 'Most Likely Real';
  } else {
    report.assessment = 'Highly Real';
  }

  // Cap score at 100
  report.score = Math.min(100, report.score);
  
  // Debug log to see final report structure
  console.log('Final Report:', {
    score: report.score,
    imagesCount: report.images.length,
    storiesCount: report.stories.length,
    redditPostsCount: report.redditPosts.length,
    socialMediaCount: Object.keys(report.socialMedia).length,
    redFlagsCount: report.redFlags.length,
    hasGravatar: report.contactInfo.emails.some(e => e.includes('Gravatar Verified'))
  });
  
  return report;
};